import { useMemo } from 'react';
import { ExternalLink, Images, Link2, X } from 'lucide-react';
import { formatRelativeDate } from '../../lib/format';

/** Matches http(s) URLs inside a message body. */
const URL_PATTERN = /(https?:\/\/[^\s<>"']+)/gi;

/**
 * Chat History drawer - the images and links shared in THIS conversation.
 *
 * Built from the messages the thread already loaded (the API has no dedicated
 * media index, and `search` only matches text). Anything the user has scrolled
 * back to load is included, so this reflects the loaded history.
 *
 * Images open in the same lightbox the inline thread uses.
 */
export default function ChatHistoryDrawer({ open, onClose, participantName, messages = [], onOpenImage }) {
  const { images, links } = useMemo(() => {
    const imageItems = [];
    const linkItems = [];
    const seenLinks = new Set();

    for (const message of messages) {
      if (message.imageUrl) {
        imageItems.push({ id: message.id, url: message.imageUrl, at: message.at });
      }

      const found = String(message.text || '').match(URL_PATTERN);
      if (found) {
        for (const url of found) {
          // Same link pasted twice should appear once.
          if (seenLinks.has(url)) continue;
          seenLinks.add(url);
          linkItems.push({
            id: `${message.id}-${seenLinks.size}`,
            url,
            at: message.at,
            from: message.mine ? 'You' : message.senderName || participantName,
          });
        }
      }
    }

    // Newest first, matching the message thread.
    imageItems.reverse();
    linkItems.reverse();
    return { images: imageItems, links: linkItems };
  }, [messages, participantName]);

  if (!open) return null;

  const isEmpty = images.length === 0 && links.length === 0;

  return (
    <>
      <button
        type="button"
        aria-label="Close chat history"
        onClick={onClose}
        tabIndex={-1}
        className="absolute inset-0 z-40 cursor-default bg-overlay backdrop-blur-[1px]"
      />

      <aside
        role="dialog"
        aria-label="Chat history"
        className="animate-z2-slide-left absolute inset-y-0 right-0 z-50 flex w-full flex-col border-l border-line bg-surface shadow-lift sm:w-[340px]"
      >
        <header className="flex items-center justify-between gap-2 border-b border-line px-4 py-3">
          <span className="inline-flex items-center gap-2 text-[13px] font-semibold text-ink">
            <Images size={15} className="text-accent" />
            Chat History
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close chat history"
            className="grid h-11 w-11 place-items-center rounded-lg text-ink-3 transition hover:bg-surface-3 hover:text-ink sm:h-8 sm:w-8"
          >
            <X size={16} />
          </button>
        </header>

        {isEmpty ? (
          <div className="flex flex-1 items-center justify-center px-6 text-center">
            <div>
              <span className="mx-auto grid h-11 w-11 place-items-center rounded-2xl bg-surface-3 text-ink-3">
                <Images size={19} />
              </span>
              <p className="mt-3 text-[12.5px] font-medium text-ink">Nothing shared yet</p>
              <p className="mt-1 text-[11.5px] leading-relaxed text-ink-3">
                Images and links sent in this conversation with {participantName} will appear here.
              </p>
            </div>
          </div>
        ) : (
          <div className="z2-scroll min-h-0 flex-1 overflow-y-auto p-4">
            {images.length > 0 && (
              <section aria-label="Shared images">
                <h3 className="pb-2 text-[11px] font-semibold tracking-wider text-ink-3 uppercase">
                  {`Images (${images.length})`}
                </h3>
                <ul className="grid grid-cols-3 gap-2">
                  {images.map((image) => (
                    <li key={image.id}>
                      <button
                        type="button"
                        onClick={() => onOpenImage(image.url)}
                        aria-label="Open image full size"
                        title={formatRelativeDate(image.at)}
                        className="group relative block w-full overflow-hidden rounded-xl border border-line"
                      >
                        <img
                          src={image.url}
                          alt="Shared in this conversation"
                          loading="lazy"
                          className="aspect-square w-full object-cover transition duration-300 group-hover:scale-105"
                        />
                        <span
                          aria-hidden="true"
                          className="absolute inset-0 bg-black/0 transition group-hover:bg-black/15"
                        />
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {links.length > 0 && (
              <section aria-label="Shared links" className={images.length > 0 ? 'mt-5' : ''}>
                <h3 className="pb-2 text-[11px] font-semibold tracking-wider text-ink-3 uppercase">
                  {`Links (${links.length})`}
                </h3>
                <ul className="space-y-1">
                  {links.map((link) => (
                    <li key={link.id}>
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-start gap-2.5 rounded-xl px-2.5 py-2.5 transition hover:bg-surface-3"
                      >
                        <Link2 size={15} className="mt-0.5 shrink-0 text-accent" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[12px] font-medium text-ink">
                            {link.url.replace(/^https?:\/\//, '')}
                          </span>
                          <span className="mt-0.5 block text-[10.5px] text-ink-3">
                            {`${link.from} · ${formatRelativeDate(link.at)}`}
                          </span>
                        </span>
                        <ExternalLink size={13} className="mt-0.5 shrink-0 text-ink-3" />
                      </a>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}
      </aside>
    </>
  );
}
